/**
 * Which pantry items a recipe uses — the one answer every ranking reads.
 *
 * The recipe screen and the grocery list decide "do I have this?" through
 * `ingredientCoverageService`: each line resolved to a canonical ingredient
 * (`resolveIngredient`, bilingual, word-anchored, longest form wins) and
 * looked up in a pantry index built the same way. The rankings did not. The
 * « Remplacer » fridge angle, the +8 per pantry item in `cookPreferences`, the
 * email's pantry dinners and the planner's inventory score each tested raw
 * text — a substring, or a 50% word overlap — so:
 *
 *  - a French shelf and an English recipe never met: "citrons" is not a
 *    substring of "2 lemons, juiced", "poulet" not of "chicken thighs";
 *  - substrings matched what they should not: "lait" in "laitue", "ail" in
 *    "caille", "pomme" in "pommes de terre";
 *  - the planner card said "uses your X" by a third rule, so the card, the
 *    ranking and the recipe screen could give three answers for one dish.
 *
 * Now each of them asks this module, and this module asks the coverage
 * service. A line "uses" a pantry item when coverage says `have` (the same
 * food, or an alternative the line names) or `probably` (the pantry holds what
 * it is made from — limes for lime juice, a chicken for its thighs). `staple`
 * and `buy` use nothing, and neither does a section label ("For the lemon
 * curd" resolves to lemons; see `isLabelForMatching`). Cooked leftovers never
 * count; that guard lives in `buildPantryIndex`, not here.
 *
 * Pure and dependency-free past `src/utils`, so the weekly email runs it on
 * the server.
 */
import {
  buildPantryIndex,
  coverageFor,
  type CoverageItem,
  type CoverageState,
  type PantryIndex,
} from './ingredientCoverageService';
import { resolveIngredient, type IngredientRef } from '../utils/resolveIngredient';
import { isLabelForMatching } from '../utils/inlineIngredientHeader';

/**
 * Resolving a line costs ~30 µs and the catalogue holds ~11k distinct lines,
 * which every generation walks for every slot. A line always resolves the
 * same way, so the answer is kept for the session. Bounded, because a
 * long-lived tab keeps importing recipes.
 */
const REF_CACHE_LIMIT = 50_000;
const refCache = new Map<string, IngredientRef>();

export function resolveLine(line: string): IngredientRef {
  const hit = refCache.get(line);
  if (hit) return hit;
  const ref = resolveIngredient(line);
  if (refCache.size >= REF_CACHE_LIMIT) refCache.clear();
  refCache.set(line, ref);
  return ref;
}

/** The fields a shelf row needs; anything else on it is passed back untouched. */
export interface MatchableItem {
  name: string;
  canonicalId?: string | null;
  status?: string;
  source?: string;
  quantity?: number;
  unit?: string;
}

/** One line against the shelf: the coverage state and the items it names. */
export interface LineMatch<T> {
  state: CoverageState;
  items: T[];
  /** A section label ("For the sauce"), not an ingredient: it uses nothing and counts for nothing. */
  label?: true;
}

export interface PantryMatcher<T> {
  /**
   * The recipe screen's answer for one line. `have` is its check mark, the
   * only state a caller may treat as certain — deducting from the pantry after
   * cooking reads this, and must not take the limes for "lime juice".
   */
  matchLine(line: string): LineMatch<T>;
  /** The pantry items one ingredient line uses (`have` or `probably`); empty when none. */
  itemsForLine(line: string): T[];
  /** The distinct pantry items a recipe's lines use, in shelf order. */
  itemsUsedBy(recipe: { ingredients?: string[] }): T[];
  /** Whether the shelf holds anything that can match at all. */
  readonly isEmpty: boolean;
}

const NONE: never[] = [];
const NO_MATCH: LineMatch<never> = { state: 'buy', items: NONE };
const LABEL: LineMatch<never> = { state: 'buy', items: NONE, label: true };

/**
 * A line's answer in a form any shelf holding the same rows can read: the
 * state, and the rows it names by key rather than by object (#865).
 */
interface SharedLineAnswer {
  state: CoverageState;
  rowKeys: readonly string[];
}
type SharedLineCache = Map<string, SharedLineAnswer>;
const SHARED_LINE_CACHE_LIMIT = 50_000;

/** What the index reads of a row. Two rows alike get an occurrence number, so a key names one row. */
function rowKeysOf(items: readonly MatchableItem[]): string[] {
  const seen = new Map<string, number>();
  return items.map((i) => {
    const base = `${i.name}\u0001${i.canonicalId ?? ''}\u0001${i.status ?? ''}\u0001${i.source ?? ''}\u0001${i.quantity ?? ''}\u0001${i.unit ?? ''}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return `${base}\u0002${n}`;
  });
}

/**
 * A matcher for one shelf. Build it once per pantry state and ask it about
 * every recipe: the index is built here, and each line's answer is cached.
 *
 * With `shared`, a line already answered for another array holding the same
 * rows is read from there and mapped onto this array's own rows — the answer
 * (what the line resolves to, which rows cover it) depends on the rows, not on
 * the array that carries them. The rows a caller gets back are always its own.
 */
export function pantryMatcher<T extends MatchableItem>(items: readonly T[], shared?: SharedLineCache): PantryMatcher<T> {
  const coverageItems: CoverageItem[] = items.map((item, i) => ({
    id: String(i),
    name: item.name,
    canonicalId: item.canonicalId,
    status: item.status,
    source: item.source,
    quantity: item.quantity,
    unit: item.unit,
  }));
  const index: PantryIndex = buildPantryIndex(coverageItems);
  const isEmpty = index.items.length === 0;
  const lineCache = new Map<string, LineMatch<T>>();
  const rowKeys = shared ? rowKeysOf(items) : NONE;
  const byRowKey = shared ? new Map(rowKeys.map((key, i) => [key, items[i]])) : null;

  const matchLine = (line: string): LineMatch<T> => {
    if (!line) return NO_MATCH;
    const cached = lineCache.get(line);
    if (cached) return cached;
    if (isLabelForMatching(line)) {
      lineCache.set(line, LABEL);
      return LABEL;
    }
    if (isEmpty) return NO_MATCH;
    let match: LineMatch<T>;
    const known = shared?.get(line);
    if (known && byRowKey) {
      const named = known.rowKeys.map((key) => byRowKey.get(key)).filter((item): item is T => !!item);
      match = { state: known.state, items: named.length > 0 ? named : NONE };
    } else {
      const result = coverageFor(resolveLine(line), index, line);
      const at = result.via.map((v) => Number(v.itemId));
      match = {
        state: result.state,
        items: at.length > 0 ? at.map((i) => items[i]).filter(Boolean) : NONE,
      };
      if (shared) {
        if (shared.size >= SHARED_LINE_CACHE_LIMIT) shared.clear();
        shared.set(line, { state: result.state, rowKeys: at.map((i) => rowKeys[i]) });
      }
    }
    lineCache.set(line, match);
    return match;
  };

  const itemsForLine = (line: string): T[] => {
    const { state, items: named } = matchLine(line);
    return state === 'have' || state === 'probably' ? named : NONE;
  };

  const itemsUsedBy = (recipe: { ingredients?: string[] }): T[] => {
    if (isEmpty || !recipe.ingredients?.length) return NONE;
    const used = new Set<T>();
    for (const line of recipe.ingredients) {
      for (const item of itemsForLine(line)) used.add(item);
    }
    if (used.size === 0) return NONE;
    return items.filter((item) => used.has(item));
  };

  return { matchLine, itemsForLine, itemsUsedBy, isEmpty };
}

const activeRowsByShelf = new WeakMap<readonly object[], readonly object[]>();

/**
 * The active rows of an inventory array, kept per array (#865). A ranking that
 * did `inventory.filter(active)` on every call handed `pantryMatcher` a new
 * array each time, so the index and every line's answer were rebuilt per
 * call: the planner's « Remplacer » cost 2.6 s on a throttled phone, and ran
 * again on every verdict tap and sync echo while the sheet was open. An item
 * with no status is active, as everywhere else in the app.
 */
export function activeRows<T extends { status?: string }>(items: readonly T[]): readonly T[] {
  let rows = activeRowsByShelf.get(items) as readonly T[] | undefined;
  if (!rows) {
    rows = items.filter((i) => !i.status || i.status === 'active');
    activeRowsByShelf.set(items, rows);
  }
  return rows;
}

const matcherByShelf = new WeakMap<readonly object[], PantryMatcher<MatchableItem>>();
/** Line answers by what the shelf holds, for an array replaced with the same rows. */
const sharedBySignature = new Map<string, SharedLineCache>();
const SHARED_CACHES_MAX = 4;

/** The shelf's rows, in whatever order a snapshot or a rehydration lists them. */
const shelfSignature = (items: readonly MatchableItem[]): string => rowKeysOf(items).sort().join('\u0000');

/**
 * `pantryMatcher`, kept per array. Redux hands every card and every ranking the
 * same inventory array until the pantry actually changes, so the index is built
 * once per pantry state. A WeakMap lets a replaced array take its matcher with it.
 *
 * The line answers are shared by content (#865): every inventory snapshot from
 * Firestore — the echo of the cook's own write included — replaces the array
 * with the same rows, and a matcher that started from nothing each time walked
 * the whole library again on the first « Remplacer » after any echo. The index
 * is rebuilt per array (a few ms for 700 rows); the answers are not.
 */
export function shelfMatcher<T extends MatchableItem>(items: readonly T[]): PantryMatcher<T> {
  let matcher = matcherByShelf.get(items) as PantryMatcher<T> | undefined;
  if (!matcher) {
    const signature = shelfSignature(items);
    let shared = sharedBySignature.get(signature);
    if (!shared) {
      shared = new Map();
      if (sharedBySignature.size >= SHARED_CACHES_MAX) {
        sharedBySignature.delete(sharedBySignature.keys().next().value as string);
      }
      sharedBySignature.set(signature, shared);
    }
    matcher = pantryMatcher(items, shared);
    matcherByShelf.set(items, matcher as PantryMatcher<MatchableItem>);
  }
  return matcher;
}

/**
 * A matcher over bare names, for the callers that hold only those. A name on
 * the shelf twice is one item, so a count stays a count of foods.
 */
export function pantryMatcherForNames(names: readonly string[]): PantryMatcher<{ name: string }> {
  const distinct = [...new Set(names.map((n) => n?.trim()).filter(Boolean))];
  return pantryMatcher(distinct.map((name) => ({ name })));
}
